/**
 * Best Next Move Panel V1
 *
 * Displays the primary recommendation from the Best Next Move engine.
 * Shows one primary recommendation with a short reason and a "Review proposal" button.
 * 
 * BOUNDARIES:
 * - Recommendation only: does not execute or approve
 * - Opens proposal review flow on button click
 * - No auto-opening of proposal flow
 * - No authority changes
 * - No execution paths
 * 
 * INVARIANT:
 * Best Next Move remains recommendation-only. Agent Proposal Flow remains the execution path.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Lightbulb } from 'lucide-react';
import { createDefaultEngine } from '@trustgate/modules/bestNextMove';
import type {
  Move,
  SystemState,
  BestNextMoveEngineOutput
} from '@trustgate/modules/bestNextMove';

interface BestNextMovePanelProps {
  onOpenProposalFlow: (move: Move) => void;
}

export const BestNextMovePanel: React.FC<BestNextMovePanelProps> = ({ onOpenProposalFlow }) => {
  const [recommendation, setRecommendation] = useState<BestNextMoveEngineOutput | null>(null);
  const [loading, setLoading] = useState(false);

  // Initialize engine and compute recommendation on mount
  useEffect(() => {
    const computeRecommendation = async () => {
      setLoading(true);
      try {
        const engine = createDefaultEngine();
        
        // Create sample system state based on current workspace state
        const systemState: SystemState = {
          timestamp: Date.now(),
          availableArtifacts: [], // Would be populated from actual workspace state
          activeBlockers: [],
          lifecycleStage: 'development',
          contextHash: 'workspace-context-v1'
        };

        // Create sample moves based on current stage
        const availableMoves: Move[] = [
          {
            moveId: 'propose-code-change',
            moveType: 'proposal',
            description: 'Propose a code change based on recent work',
            dependencies: [],
            unblocks: [],
            riskLevel: 'low',
            estimatedDurationMs: 60000,
            metadata: { priority: 80 }
          },
          {
            moveId: 'review-memory',
            moveType: 'review',
            description: 'Review and organize your AI memory artifacts',
            dependencies: [],
            unblocks: [],
            riskLevel: 'low',
            estimatedDurationMs: 30000,
            metadata: { priority: 60 }
          },
          {
            moveId: 'connect-github',
            moveType: 'integration',
            description: 'Connect GitHub to import work history',
            dependencies: [],
            unblocks: ['propose-code-change'],
            riskLevel: 'low',
            estimatedDurationMs: 120000,
            metadata: { priority: 90 }
          }
        ];

        const input = {
          systemState,
          availableMoves,
          blockedMoves: []
        };

        const output = engine.selectBestNextMove(input);
        setRecommendation(output);
      } catch (error) {
        console.error('[BestNextMove] Failed to compute recommendation:', error);
      } finally {
        setLoading(false);
      }
    };

    computeRecommendation();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-6 mb-8">
        <div className="flex items-center space-x-3">
          <Sparkles className="w-5 h-5 text-purple-400 animate-pulse" />
          <span className="text-gray-400">Computing best next move...</span>
        </div>
      </div>
    );
  }

  if (!recommendation || !recommendation.selectedMove) {
    return (
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-700 rounded-xl p-6 mb-8">
        <div className="flex items-center space-x-3">
          <Lightbulb className="w-5 h-5 text-purple-400" />
          <span className="text-gray-400">No recommendations available at this time.</span>
        </div>
      </div>
    );
  }

  const selectedMove = recommendation.selectedMove;
  const confidence = recommendation.confidence;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-2 border-purple-500 rounded-xl p-6 mb-8"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <h2 className="text-xl font-semibold text-white">Best Next Move</h2>
            <span className="text-xs text-purple-300 bg-purple-900/50 px-3 py-1 rounded-full border border-purple-600">
              {confidence.toFixed(0)}% confidence
            </span>
          </div>
          
          <p className="text-lg font-medium text-white mb-2">{selectedMove.description}</p>
          
          {/* Why this move? */}
          <div className="mb-3 p-3 bg-purple-900/20 border border-purple-700 rounded-lg">
            <p className="text-sm text-purple-200 mb-1 font-medium">Why this move?</p>
            <p className="text-sm text-gray-300">{recommendation.reason}</p>
          </div>
          
          <div className="flex items-center space-x-4 text-xs text-gray-400 mb-4">
            <span className="flex items-center space-x-1">
              <span>Risk:</span>
              <span className={`${
                selectedMove.riskLevel === 'low' ? 'text-green-400' : 
                selectedMove.riskLevel === 'medium' ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {selectedMove.riskLevel}
              </span>
            </span>
            {selectedMove.estimatedDurationMs && (
              <span className="flex items-center space-x-1">
                <span>Duration:</span>
                <span>{Math.round(selectedMove.estimatedDurationMs / 60000)} min</span>
              </span>
            )}
          </div>

          {/* See alternatives collapsed section */}
          {recommendation.alternatives.length > 0 && (
            <div className="border-t border-purple-700 pt-3 mt-3">
              <button
                onClick={() => setShowAlternatives(!showAlternatives)}
                className="text-sm text-purple-300 hover:text-purple-200 flex items-center space-x-1"
              >
                <span>{showAlternatives ? 'Hide' : 'See'} alternatives</span>
                <span>{showAlternatives ? '▲' : '▼'}</span>
              </button>
              
              {showAlternatives && (
                <div className="mt-3 space-y-2">
                  {recommendation.alternatives.map((alt) => (
                    <div
                      key={alt.move.moveId}
                      className="bg-gray-900/50 border border-gray-700 rounded-lg p-3"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white mb-1">{alt.move.description}</p>
                          <p className="text-xs text-gray-400 mb-2">{alt.whyNotSelected}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          alt.move.riskLevel === 'low' ? 'bg-green-900/30 text-green-400 border border-green-700' : 
                          alt.move.riskLevel === 'medium' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700' : 
                          'bg-red-900/30 text-red-400 border border-red-700'
                        }`}>
                          {alt.move.riskLevel}
                        </span>
                      </div>
                      <button
                        onClick={() => onOpenProposalFlow(alt.move)}
                        className="text-xs text-purple-300 hover:text-purple-200 flex items-center space-x-1"
                      >
                        <span>Review proposal</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={() => onOpenProposalFlow(selectedMove)}
          className="ml-6 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 whitespace-nowrap"
        >
          <span>Review proposal</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};
